import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { categories } from "../mock";
import API from "../api";
import { Clock, Plus, Package, TrendingUp, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SellerDashboard = () => {
  const { t, tl } = useApp();
  const { user } = useAuth();
  const [tab, setTab] = useState("add");
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [form, setForm] = useState({ name: "", desc: "", brand: "", category: "women", price: "", oldPrice: "", image: "", sizes: "", colors: "", moq: "", unit: "", tiers: "" });
  const [msg, setMsg] = useState("");

  const load = () => {
    API.get("/vendor/products").then((r) => setProducts(r.data)).catch(() => {});
    API.get("/vendor/orders").then((r) => setSales(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!user) return null;

  if (user.vendor_status !== "approved") {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center fade-up">
        <Clock className="h-12 w-12 mx-auto mb-4 text-[#b08d57]" strokeWidth={1.2} />
        <h1 className="font-serif-lux text-3xl mb-3">{user.shop_name || t("sellerSpace")}</h1>
        <p className="text-[#555]">Votre boutique est en attente de validation par un administrateur. Vous pourrez ajouter vos produits dès l'approbation.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const payload = {
        name: form.name, desc: form.desc, brand: form.brand, category: form.category,
        price: parseFloat(form.price), oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : null,
        image: form.image,
        sizes: form.sizes ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
        colors: form.colors ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (form.category === "negoce") {
        payload.moq = form.moq ? parseInt(form.moq, 10) : null;
        payload.unit = form.unit || null;
        payload.tiers = form.tiers
          ? form.tiers.split(",").map((s) => s.trim()).filter(Boolean).map((pair) => {
              const [qty, price] = pair.split(":").map((v) => parseFloat(v.trim()));
              return { qty: parseInt(qty, 10), price };
            }).filter((tt) => !isNaN(tt.qty) && !isNaN(tt.price))
          : null;
      }
      await API.post("/products", payload);
      setForm({ name: "", desc: "", brand: "", category: "women", price: "", oldPrice: "", image: "", sizes: "", colors: "", moq: "", unit: "", tiers: "" });
      setMsg("Produit soumis — en attente de validation admin.");
      load();
      setTab("products");
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Erreur.");
    }
  };

  const del = async (id) => { await API.delete(`/products/${id}`); load(); };

  const inputCls = "w-full px-4 py-3 bg-white border border-border rounded-sm text-sm outline-none focus:border-[#b08d57] transition-colors";
  const badge = (s) => s === "approved" ? "bg-[#2f7d4f]/10 text-[#2f7d4f]" : s === "pending" ? "bg-[#b08d57]/15 text-[#8a6a2f]" : "bg-[#8b1e3f]/10 text-[#8b1e3f]";
  const totalNet = sales.reduce((s, o) => s + o.net, 0);

  const tabs = [{ id: "add", label: t("add") || "Ajouter", icon: Plus }, { id: "products", label: "Mes produits", icon: Package }, { id: "sales", label: "Mes ventes", icon: TrendingUp }];

  return (
    <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h1 className="font-serif-lux text-4xl mb-1">{user.shop_name}</h1>
      <p className="text-sm text-muted-foreground mb-8">{t("sellerSpace")}</p>

      <div className="flex gap-2 border-b border-border mb-8">
        {tabs.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === tb.id ? "border-[#b08d57] text-[#1b1b1b]" : "border-transparent text-muted-foreground hover:text-[#1b1b1b]"}`}>
            <tb.icon className="h-4 w-4" strokeWidth={1.5} /> {tb.label}
          </button>
        ))}
      </div>

      {tab === "add" && (
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-2xl">
          <input data-testid="prod-name" className={inputCls} placeholder="Nom du produit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="prod-brand" className={inputCls} placeholder="Marque" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} required />
          <textarea data-testid="prod-desc" className={inputCls + " md:col-span-2"} rows={3} placeholder="Description" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} required />
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-white rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{categories.map((c) => (<SelectItem key={c.id} value={c.id}>{t(c.key)}</SelectItem>))}</SelectContent>
          </Select>
          <input data-testid="prod-image" className={inputCls} placeholder="URL de l'image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required />
          <input data-testid="prod-price" className={inputCls} type="number" step="0.01" placeholder="Prix (€)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          <input className={inputCls} type="number" step="0.01" placeholder="Ancien prix (optionnel)" value={form.oldPrice} onChange={(e) => setForm({ ...form, oldPrice: e.target.value })} />
          <input className={inputCls} placeholder="Tailles (ex: S, M, L)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />
          <input className={inputCls} placeholder="Couleurs (ex: #000, #fff)" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} />
          {form.category === "negoce" && (
            <>
              <input data-testid="prod-moq" className={inputCls} type="number" placeholder={t("moqInput")} value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} />
              <input data-testid="prod-unit" className={inputCls} placeholder={t("unitInput")} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              <input data-testid="prod-tiers" className={inputCls + " md:col-span-2"} placeholder={t("tiersInput")} value={form.tiers} onChange={(e) => setForm({ ...form, tiers: e.target.value })} />
            </>
          )}
          {msg && <p className="md:col-span-2 text-sm text-[#b08d57]">{msg}</p>}
          <button type="submit" data-testid="prod-submit" className="md:col-span-2 bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase hover:bg-[#b08d57] transition-colors">Soumettre le produit</button>
        </form>
      )}

      {tab === "products" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.length === 0 && <p className="text-muted-foreground text-sm">Aucun produit.</p>}
          {products.map((p) => (
            <div key={p.id} className="border border-border rounded-sm bg-white overflow-hidden">
              <img src={p.image} alt="" className="w-full aspect-[3/4] object-cover" />
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(p.status)}`}>{p.status}</span>
                  <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-[#8b1e3f]"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
                </div>
                <p className="text-sm mt-1.5 line-clamp-1">{tl(p.name)}</p>
                <p className="text-sm font-medium">{p.price} €</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "sales" && (
        <div>
          <div className="bg-[#f2ede4] rounded-sm p-5 mb-6 flex items-center justify-between">
            <span className="text-sm text-[#555]">Revenu net (après commission 10%)</span>
            <span className="font-serif-lux text-2xl">{totalNet.toFixed(2)} €</span>
          </div>
          <div className="space-y-3">
            {sales.length === 0 && <p className="text-muted-foreground text-sm">{t("noOrders")}</p>}
            {sales.map((o) => (
              <div key={o.id} className="border border-border rounded-sm p-4 bg-white text-sm">
                <div className="flex justify-between"><span>#{o.id.slice(0, 8).toUpperCase()}</span><span className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span></div>
                <div className="flex justify-between mt-2 text-[#555]"><span>Brut: {o.gross.toFixed(2)} €</span><span>Commission: -{o.commission.toFixed(2)} €</span><span className="font-medium text-[#1b1b1b]">Net: {o.net.toFixed(2)} €</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerDashboard;
